import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "../utils/axios";
import { ChatHistoryAPI } from "@/utils/axios";

interface Conversation {
  id: string;
  title: string | null;
  project_id: number | null;
  status: string;
  last_activity: string | null;
}

interface PaginatedConversations {
  total: number;
  limit: number;
  offset: number;
  conversations: Conversation[];
}

/**
 * Hook: useConversations
 * Fetches conversations either global (projectId === null) or for a specific project.
 * Uses limit/offset pagination wired to react-query's useInfiniteQuery.
 */
export function useConversations(projectId: number | null, pageSize = 5) {
  const fetchPage = async ({ pageParam = 0 }): Promise<PaginatedConversations> => {
    const isValidProject = projectId !== null && !Number.isNaN(projectId);
    const baseUrl = isValidProject
      ? `/v1/chat/projects/${projectId}/history`
      : "/v1/chat/history";
    const { data } = await axios.get<PaginatedConversations>(baseUrl, {
      params: { limit: pageSize, offset: pageParam },
    });
    return data;
  };

  return useInfiniteQuery({
    queryKey: ["conversations", projectId],
    queryFn: fetchPage,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    initialPageParam: 0,
  });
}

// ---------------------------------------------------------------------------

interface ConversationMessage {
  id: string;
  role: string;
  content: string;
  timestamp: string;
  metadata: Record<string, unknown>;
}

interface PaginatedMessages {
  total: number;
  limit: number;
  offset: number;
  messages: ConversationMessage[];
}

export function useConversationMessages(conversationId: string, pageSize = 5) {
  const fetchPage = async ({ pageParam = 0 }): Promise<PaginatedMessages> => {
    const { data } = await axios.get<PaginatedMessages>(
      `/v1/chat/history/${conversationId}/messages`,
      {
        params: { limit: pageSize, offset: pageParam },
      }
    );
    return data;
  };

  return useInfiniteQuery({
    queryKey: ["conversationMessages", conversationId],
    queryFn: fetchPage,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    staleTime: 1000 * 30, // 30s
    initialPageParam: 0,
  });
}

// ---------------------------------------------------------------------------

/**
 * Hook: useDeleteConversation
 * Provides a mutation to soft-delete a conversation and automatically refresh the list cache.
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      token,
    }: {
      conversationId: string;
      token?: string;
    }) => {
      await ChatHistoryAPI.deleteConversation(conversationId, token);
      return conversationId;
    },
    onMutate: async ({ conversationId }) => {
      await queryClient.cancelQueries({
        queryKey: ["conversations"],
        exact: false,
      });
      const prev = queryClient.getQueriesData({
        queryKey: ["conversations"],
        exact: false,
      });
      // optimistic removal from each cached page
      prev.forEach(([key, data]) => {
        const typed = data as any;
        if (!typed || !typed.pages) return;
        const newPages = typed.pages.map((page: any) => ({
          ...page,
          conversations: page.conversations.filter(
            (c: any) => c.id !== conversationId
          ),
        }));
        queryClient.setQueryData(key, { ...typed, pages: newPages });
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        context.prev.forEach(([key, data]: any) => {
          queryClient.setQueryData(key, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["conversations"],
        exact: false,
      });
    },
  });
}
